/**
 * D7 (SAD-62) money-label vocabulary for the Periods-tab and its KPIs — the
 * one shared home so wording cannot drift per surface. See
 * major-push/august-roadmap/_decisions.md, "D7 - Periods-tab money semantics".
 *
 * ceiling / currentTcv have no consumer here yet: the contract-level Ceiling
 * / Current TCV labels stay as they are today. They live in this module
 * anyway so that when those surfaces are built, nothing retypes this copy.
 */ export const MONEY_LABELS = {
    funded: 'Funded',
    totalValue: 'Total value',
    ceiling: 'Ceiling',
    currentTcv: 'Current TCV'
};
// Where period money comes from — the create dialog's description (and any
// future surface that must say it). One home, same reason as MONEY_TIPS.
export const MONEY_PROVENANCE = 'Funded and Total value are written by contract modifications — a new period starts unfunded.';
export const MONEY_TIPS = {
    // C2 deferral: the FAR-obligated figure (SF1449 box-26) is not yet plumbed into
    // funded_amount — deferred to the IDP-06 period-extraction lane (needs proto/gen).
    funded: "Money the government has placed on the contract so far — what you can bill against today. Increases when a funding modification is uploaded. (This is the contract's 'obligated' amount in FAR/USASpending terms.)",
    totalValue: 'The full priced value of this period in the contract schedule. Funding arrives toward this amount through modifications — it is a promise for the period, not money you can bill yet.',
    ceiling: 'The maximum the contract can be worth if every option period and optional task is exercised. Changes only when a modification adds scope.',
    currentTcv: "The contract's running total value as modifications land.",
    fundedToDate: "Running total of funded dollars after each modification. Climbs toward the period's total value.",
    totalValueToDate: "Running total of this period's priced value after each modification. Still a promise for the period, not money you can bill yet."
};
// SAD-166/SAD-176 — Financial Health tab (Delivery/Financial/FinancialHealthTab).
// Same one-shared-home reasoning as the labels/tips above: burn/AR/funding
// wording must not drift between the headline row, the progress section, and
// the history table.
export const FINANCIAL_HEALTH_LABELS = {
    monthlyBurnRate: 'Monthly burn rate',
    accountsReceivable: 'Accounts receivable',
    fundingAvailable: 'Total available funding',
    fundingConsumed: 'Funding consumed',
    fundingRemaining: 'Funding remaining',
    periodOfPerformance: 'Period of performance',
    fundingAlertThreshold: 'Funding alert threshold'
};
export const FINANCIAL_HEALTH_TIPS = {
    monthlyBurnRate: 'The most recently recorded monthly burn rate, for the labeled month.',
    accountsReceivable: 'The most recently recorded accounts receivable balance, for the labeled month.',
    fundingAvailable: 'The total funding recorded as available to this program to date.',
    // D7's prescribed burn-indicator wording, adapted to the program's
    // configured threshold rather than hard-coding 75% — a program that has
    // overridden the default still sees an accurate marker.
    fundingConsumedForThreshold: (thresholdPct)=>`Compares what you've billed/spent against the funded amount. Under FAR 52.232-22 the contractor must notify the CO when expected costs approach ${thresholdPct}% of funded — treat the ${thresholdPct}% marker as a hard signal, not a suggestion.`,
    fundingRemaining: 'Available funding minus funding consumed. A negative figure is a real overrun, not a rounding artifact.',
    popElapsed: "How far the program is through its current period of performance's calendar days."
};
// SAD-137 — the per-period modification-history table and its funding
// timeline. Same one-shared-home rule D7 puts on MONEY_LABELS/MONEY_TIPS: the
// column headers and their tooltips live here so the table, the timeline and
// any future surface cannot drift apart.
//
// Matt's bounce (2026-08-27) names these five columns verbatim; the wording
// below is theirs, not a paraphrase.
export const MOD_HISTORY_LABELS = {
    modNumber: 'MOD #',
    effectiveDate: 'Effective Date',
    popChange: 'Change in PoP',
    fundedDelta: 'Funded Delta',
    totalValueDelta: 'Total Value Delta',
    // The initial contract is shown in the MOD # column as 'Award' rather than a
    // modification number — it is what every later delta is measured against.
    award: 'Award',
    // The trailing row restates today's absolute figures instead of a change.
    current: 'Current',
    // Matt's mockup titles the timeline block and its right-hand column, and
    // closes with a net-change line. Same shared module as everything else so the
    // wording cannot drift per surface (D7).
    timelineHeading: 'Funding impact over time',
    runningFundedTotal: 'Running funded total',
    netFundingChange: 'Net funding change since award',
    // Shown in Change in PoP when a modification moved money but not dates.
    noPopChange: 'No change'
};
export const MOD_HISTORY_TIPS = {
    modNumber: "The modification that made this change, by its contract modification number. The first row reads 'Award' — the initial contract every later modification changes.",
    effectiveDate: 'The date the modification took effect on the contract, which is not necessarily the date it was recorded here. Blank on the final row, which restates current values rather than a change.',
    popChange: "How this modification moved the period's start or end date. Blank when the modification changed money only. The final row shows the period of performance as it stands today.",
    fundedDelta: "How much funding this modification added to (or removed from) this period — the change, not the running total. The final row shows the period's current funded amount.",
    totalValueDelta: "How much this modification changed this period's total contract value — the change, not the running total. The final row shows the period's current total value.",
    timeline: "Each modification's funding change for this period, in the order the changes were applied, with the funded amount running total after each one."
};
